import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash, Search, LayoutGrid, List } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CategoriasTab({ ligaId, ligaName }) {
  const [categorias, setCategorias] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [name, setName] = useState('');
  const [tipoEdad, setTipoEdad] = useState('');
  const [valorEdad, setValorEdad] = useState('');
  const [criterioEdad, setCriterioEdad] = useState('');
  const [maxExcepciones, setMaxExcepciones] = useState('0');
  const [valorExcepcion, setValorExcepcion] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    if (ligaId) {
      loadData();
    }
  }, [ligaId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/categorias?liga_id=${ligaId}`, { withCredentials: true });
      setCategorias(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { 
        name, 
        liga_id: ligaId,
        tipo_edad: tipoEdad || null,
        valor_edad: valorEdad ? parseInt(valorEdad) : null,
        criterio_edad: criterioEdad || null,
        max_excepciones: maxExcepciones ? parseInt(maxExcepciones) : 0,
        valor_excepcion: valorExcepcion ? parseInt(valorExcepcion) : null
      };
      
      if (editingCategoria) {
        await axios.put(`${API}/categorias/${editingCategoria.id}`, payload, { withCredentials: true });
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categorias`, payload, { withCredentials: true });
        toast.success('Categoría creada');
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setTipoEdad('');
    setValorEdad('');
    setCriterioEdad('');
    setMaxExcepciones('0');
    setValorExcepcion('');
    setEditingCategoria(null);
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setName(categoria.name);
    setTipoEdad(categoria.tipo_edad || '');
    setValorEdad(categoria.valor_edad?.toString() || '');
    setCriterioEdad(categoria.criterio_edad || '');
    setMaxExcepciones(categoria.max_excepciones?.toString() || '0');
    setValorExcepcion(categoria.valor_excepcion?.toString() || '');
    setShowDialog(true);
  };

  const handleDelete = async (categoriaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;
    try {
      await axios.delete(`${API}/categorias/${categoriaId}`, { withCredentials: true });
      toast.success('Categoría eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Categorias</h2>
          <p className="text-sm text-slate-400">Liga: {ligaName}</p>
        </div>
        <Button
          data-testid="create-categoria-btn"
          onClick={() => {
            setEditingCategoria(null);
            setName('');
            setShowDialog(true);
          }}
          className="gap-2 bg-white text-slate-900 hover:bg-slate-200"
        >
          <Plus className="w-4 h-4" />
          Nueva Categoria
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar categoria..." className="pl-9 bg-slate-700 border-slate-600 text-white"
            data-testid="categorias-search" />
        </div>
        <div className="flex border border-slate-600 rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('cards')}
            className={`p-2 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="categorias-view-cards"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="categorias-view-list"><List className="w-4 h-4" /></button>
        </div>
      </div>

      {(() => {
        const filtered = categorias.filter(c =>
          !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filtered.length === 0) {
          return <div className="text-center py-12 text-slate-500">{searchTerm ? 'Sin resultados' : 'No hay categorias creadas para esta liga'}</div>;
        }

        if (viewMode === 'list') {
          return (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                  <th className="px-4 py-3 text-left">Nombre</th>
                  <th className="px-4 py-3 text-left">Requisito</th>
                  <th className="px-4 py-3 text-left">Excepciones</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {filtered.map(cat => (
                    <tr key={cat.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-white font-medium">{cat.name}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {cat.tipo_edad && cat.valor_edad ? `${cat.tipo_edad === 'sub' ? '<' : '≥'} ${cat.valor_edad}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {cat.max_excepciones > 0 ? `${cat.max_excepciones} jug.` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button onClick={() => handleEdit(cat)} variant="outline" size="sm" className="h-7 px-2 border-slate-600 text-slate-300"><Edit className="w-3 h-3" /></Button>
                          <Button onClick={() => handleDelete(cat.id)} variant="destructive" size="sm" className="h-7 px-2"><Trash className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((categoria) => (
            <Card key={categoria.id} className="p-4 hover:shadow-md transition-shadow bg-slate-800 border-slate-700" data-testid="categoria-card">
              <h3 className="font-semibold text-lg mb-2 text-white">{categoria.name}</h3>
              {categoria.tipo_edad && categoria.valor_edad && (
                <div className="text-sm text-slate-400 space-y-1 mb-2">
                  <p>
                    <span className="font-medium text-slate-300">Requisito:</span>{' '}
                    {categoria.tipo_edad === 'sub' ? '<' : '≥'} {categoria.valor_edad} ({categoria.criterio_edad === 'edad_cumplida' ? 'edad cumplida' : 'ano nacimiento'})
                  </p>
                  {categoria.max_excepciones > 0 && categoria.valor_excepcion && (
                    <p className="text-amber-400">
                      <span className="font-medium">Excepciones:</span> Hasta {categoria.max_excepciones} jugadores{' '}
                      {categoria.tipo_edad === 'sub' ? '<' : '≥'} {categoria.valor_excepcion}
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-2 mt-3">
              <Button
                data-testid={`edit-categoria-${categoria.id}`}
                onClick={() => handleEdit(categoria)}
                variant="outline"
                size="sm"
                className="gap-2 flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Edit className="w-4 h-4" />
                Editar
              </Button>
              <Button
                data-testid={`delete-categoria-${categoria.id}`}
                onClick={() => handleDelete(categoria.id)}
                variant="destructive"
                size="sm"
                className="gap-2 flex-1"
              >
                <Trash className="w-4 h-4" />
                Eliminar
              </Button>
            </div>
          </Card>
          ))
        }
      </div>
        );
      })()}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="categoria-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCategoria ? 'Editar Categoria' : 'Nueva Categoria'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label className="text-slate-300">Liga</Label>
              <Input value={ligaName} disabled className="bg-slate-700 border-slate-600 text-slate-400" />
            </div>
            <div>
              <Label className="text-slate-300" htmlFor="name">Nombre de la Categoria *</Label>
              <Input
                data-testid="categoria-name-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ej: Sub 15, Adultos, etc."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            <div className="border-t border-slate-700 pt-4">
              <h4 className="font-semibold text-sm mb-3 text-slate-300">Requisitos de Edad (opcional)</h4>
              
              <div className="space-y-3">
                {/* Paso 1: Tipo de Edad */}
                <div>
                  <Label className="text-slate-300">1. Tipo de Edad</Label>
                  <Select value={tipoEdad || "sin_restriccion"} onValueChange={(val) => setTipoEdad(val === "sin_restriccion" ? "" : val)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_restriccion">Sin restricción</SelectItem>
                      <SelectItem value="sub">Sub (menor que)</SelectItem>
                      <SelectItem value="mayor_igual">Mayor o igual que</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Paso 2: Criterio */}
                <div>
                  <Label className="text-slate-300">2. Criterio de Validacion</Label>
                  <Select value={criterioEdad || undefined} onValueChange={setCriterioEdad} disabled={!tipoEdad || tipoEdad === "sin_restriccion"}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Seleccionar criterio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="edad_cumplida">Edad cumplida</SelectItem>
                      <SelectItem value="año_nacimiento">Año de nacimiento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Paso 3: Valor */}
                <div>
                  <Label className="text-slate-300">3. Valor (Requisito Normal)</Label>
                  <Input
                    type="number"
                    value={valorEdad}
                    onChange={(e) => setValorEdad(e.target.value)}
                    placeholder="Ej: 15, 18, etc."
                    disabled={!tipoEdad || tipoEdad === "sin_restriccion"}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {tipoEdad === 'sub' && 'Edad máxima para cumplir normalmente'}
                    {tipoEdad === 'mayor_igual' && 'Edad mínima para cumplir normalmente'}
                  </p>
                </div>

                {/* Excepciones */}
                <div className="border-t border-slate-700 pt-3">
                  <h5 className="font-semibold text-sm mb-2 text-slate-300">Excepciones</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">Cantidad Maxima</Label>
                      <Input
                        type="number"
                        value={maxExcepciones}
                        onChange={(e) => setMaxExcepciones(e.target.value)}
                        min="0"
                        placeholder="0"
                        disabled={!tipoEdad || tipoEdad === "sin_restriccion"}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Valor Maximo Excepcion</Label>
                      <Input
                        type="number"
                        value={valorExcepcion}
                        onChange={(e) => setValorExcepcion(e.target.value)}
                        placeholder="Ej: 17"
                        disabled={!tipoEdad || tipoEdad === "sin_restriccion" || !maxExcepciones || maxExcepciones === '0'}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {tipoEdad === 'sub' && 'Jugadores entre el valor normal y el valor de excepción pueden entrar como excepción'}
                    {tipoEdad === 'mayor_igual' && 'Jugadores entre el valor de excepción y el valor normal pueden entrar como excepción'}
                    {!tipoEdad && 'Configura primero el tipo, criterio y valor para habilitar excepciones'}
                  </p>
                </div>
              </div>
            </div>

            <Button data-testid="categoria-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
